import { useEffect, useState } from 'react'
import { API_BASE_URL } from '../../../config'

interface Preview { title: string; description: string; image: string | null }
const cache = new Map<string, { expires: number; value: Preview | null }>()

const services = [
    { key: 'telegram', name: 'Telegram', domains: ['t.me', 'telegram.me'], action: 'Открыть в Telegram', icon: 'tg.svg' },
    { key: 'vk', name: 'ВКонтакте', domains: ['vk.com', 'vk.ru'], action: 'Перейти в сообщество', icon: 'vk.svg' },
    { key: 'whatsapp', name: 'WhatsApp', domains: ['wa.me', 'whatsapp.com'], action: 'Написать', icon: 'wa.svg' },
    { key: 'max', name: 'MAX', domains: ['max.ru'], action: 'Открыть в MAX', icon: 'max.svg' },
    { key: 'youtube', name: 'YouTube', domains: ['youtube.com', 'youtu.be'], action: 'Смотреть', icon: null },
    { key: 'yandex', name: 'Яндекс', domains: ['yandex.ru', 'yandex.com', 'ya.ru'], action: 'Открыть в Яндексе', icon: 'ya.svg' },
    { key: 'gis', name: '2ГИС', domains: ['2gis.ru', '2gis.com'], action: 'Посмотреть на карте', icon: 'gis.svg' },
    { key: 'avito', name: 'Авито', domains: ['avito.ru'], action: 'Посмотреть объявление', icon: 'avito.png' },
    { key: 'ozon', name: 'Ozon', domains: ['ozon.ru'], action: 'Посмотреть товар', icon: 'ozon.svg' },
    { key: 'wb', name: 'Wildberries', domains: ['wildberries.ru', 'wb.ru'], action: 'Посмотреть товар', icon: 'wb.svg' },
]

export const describeLink = (link: string) => {
    try {
        const url = new URL(link)
        const host = url.hostname.replace(/^www\./, '')
        const service = services.find(({domains}) => domains.some(domain => host === domain || host.endsWith(`.${domain}`)))
        if (service && (url.protocol === 'https:' || url.protocol === 'http:')) return {...service, host, href: url.href}
        if (url.protocol === 'tel:' || url.protocol === 'mailto:') {
            const phone = url.protocol === 'tel:'
            return {key: 'contact', name: phone ? 'Телефон' : 'Почта', host: url.pathname, action: phone ? 'Позвонить' : 'Написать', icon: null, href: url.href}
        }
        if (url.protocol === 'https:' || url.protocol === 'http:') {
            return {key: 'website', name: 'Сайт', host, action: 'Перейти на сайт', icon: null, href: url.href}
        }
    } catch { /* Invalid links remain visible but cannot execute a custom scheme. */ }
    return {key: 'website', name: 'Ссылка', host: '', action: 'Ссылка недоступна', icon: null, href: undefined}
}

export const useLinkPreview = (id: number, link: string) => {
    const key = `${id}:${link}`
    const [state, setState] = useState<{key: string; value: Preview | null}>({key, value: null})
    useEffect(() => {
        if (!link.startsWith('https://')) return
        const cached = cache.get(key)
        if (cached && cached.expires > Date.now()) {
            setState({key, value: cached.value})
            return
        }
        const controller = new AbortController()
        const timeout = window.setTimeout(() => controller.abort(), 15000)
        fetch(`${API_BASE_URL}/link-preview?link_id=${id}`, {signal: controller.signal})
            .then(response => response.ok ? response.json() : null)
            .then(data => {
                if (controller.signal.aborted) return
                const raw = data?.preview
                const value: Preview | null = raw ? {
                    title: typeof raw.title === 'string' ? raw.title : '',
                    description: typeof raw.description === 'string' ? raw.description : '',
                    image: typeof raw.image === 'string' && raw.image.startsWith('https://') ? raw.image : null,
                } : null
                if (cache.size >= 200) cache.delete(cache.keys().next().value as string)
                cache.set(key, {expires: Date.now() + (value ? 300000 : 60000), value})
                setState({key, value})
            })
            .catch(() => { /* Keep the local card when the preview is unavailable. */ })
            .finally(() => window.clearTimeout(timeout))
        return () => { controller.abort(); window.clearTimeout(timeout) }
    }, [id, link, key])
    return state.key === key ? state.value : null
}
